import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { itsRequest } from 'http/authConfig';
import PublicShell from './PublicShell';
import { useTranslation } from 'react-i18next';
import Bluu1 from 'assets/landing/Bluu_1.PNG';
import Bluu2 from 'assets/landing/Bluu_2.PNG';
import Bluu3 from 'assets/landing/Bluu_3.PNG';
import Bluu4 from 'assets/landing/Bluu_4.PNG';
import App0 from 'assets/landing/app_0.PNG';
import App1 from 'assets/landing/app_1.PNG';
import App2 from 'assets/landing/app_2.PNG';
import App3 from 'assets/landing/app_3.PNG';
import App4 from 'assets/landing/app_4.PNG';
import App5 from 'assets/landing/app_5.PNG';
import App6 from 'assets/landing/app_6.PNG';
import useWindowsDimension from 'functions/useWindowsDimension';

const LandingPage: React.FC = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { height } = useWindowsDimension();
  const [slide, setSlide] = useState(0);
  const slides = [Bluu1, Bluu2, Bluu3, Bluu4];
  const mobileShots = [App0, App1, App2, App3, App4, App5, App6];
  const bragLines =
    (t('landing.panel.brag', { returnObjects: true }) as string[]) || [];
  const [mobileSlide, setMobileSlide] = useState(0);
  
  const partnerListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    const id = setInterval(() => {
      setMobileSlide((prev) => (prev + 1) % mobileShots.length);
    }, 4000);
    return () => clearInterval(id);
  }, [mobileShots.length]);

  useEffect(() => {
    const el = partnerListRef.current;
    if (!el) return;

    const duration = 120000; // 120s per full scroll
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const maxScroll = el.scrollHeight - el.clientHeight;

      // Reset when we reach the bottom
      const normalized = progress % duration;
      const pct = normalized / duration;
      el.scrollTop = pct * maxScroll;

      requestAnimationFrame(step);
    };

    const rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const handleEnterApp = () => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }
    instance.loginRedirect(itsRequest);
  };

  const handleRegister = () => {
    instance.loginRedirect(itsRequest);
  };

  const handleLogin = () => {
    instance.loginRedirect(itsRequest);
  };

  const partnerListHeight = height / 2 - 180;

  const heroImageMaxHeight = height >= 1200 ? 555 : height >= 900 ? 333 : 24;
  const chipFontsize = height >= 1200 ? 32 : height >= 900 ? 24 : 16;
  const headerFontsize = height >= 1200 ? 64 : height >= 900 ? 48 : 24;
  const subFontsize = height >= 1200 ? 30 : height >= 900 ? 22 : 16;
  const rightFontsize = height >= 1200 ? 22 : height >= 900 ? 14 : 14;
  return (
    <PublicShell>
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          minHeight: height - 180,
          maxHeight: height - 180,
          overflow: 'visible',
        }}
      >
        <Grid item xs={12} md={5.5}>
          <Grid
            container
            sx={{
              minHeight: 'auto',
              maxHeight: 'none',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              px: 1,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                minHeight: 40,
                maxHeight: 40,
                mb: 2,
              }}
            >
              <Chip
                label={t(
                  'landing.hero.tagline1',
                  'Future of IT management is here'
                )}
                color='secondary'
                sx={{ width: 'fit-content', fontSize: chipFontsize }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                height: (headerFontsize + 10) * 3,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: 1,
                  fontSize: headerFontsize,
                }}
              >
                {t('landing.hero.title')}
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                height: (subFontsize + 10) * 4,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  lineHeight: 1.05,
                  letterSpacing: 1,
                  fontSize: subFontsize,
                }}
                color='text.secondary'
              >
                {t('landing.hero.body')}
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              display='flex'
              flexDirection={'column'}
              justifyContent='center'
              alignItems='center'
            >
              <Box
                component='img'
                src={slides[slide]}
                alt='Bluu skjermbilde'
                sx={{
                  maxHeight: {
                    xs: 200,
                    sm: 250,
                    md: heroImageMaxHeight,
                    lg: heroImageMaxHeight,
                    xl: heroImageMaxHeight,
                  },
                  transition: 'opacity 0.4s ease',
                  width: '100%',
                  objectFit: 'contain',
                }}
              />
              <Stack
                direction='row'
                spacing={2}
                sx={{ mt: 1, height: 20, alignItems: 'center' }}
              >
                {slides.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSlide(idx)}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      backgroundColor:
                        idx === slide ? 'secondary.main' : 'primary.lighter',
                      opacity: idx === slide ? 1 : 0.6,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid
            container
            sx={{
              minHeight: 'auto',
              maxHeight: 'none',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              px: 1,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                minHeight: 40,
                maxHeight: 40,
                mb: 2,
              }}
            >
              <Chip
                label={t(
                  'landing.hero.tagline2',
                  'give your customers the service they deserve'
                )}
                color='secondary'
                sx={{ width: 'fit-content', fontSize: chipFontsize }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                height: (headerFontsize + 10) * 3,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: 1,
                  fontSize: headerFontsize,
                }}
              >
                {t('landing.mobile.heading')}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                height: (subFontsize + 10) * 4,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  lineHeight: 1.05,
                  letterSpacing: 1,
                  fontSize: subFontsize,
                }}
                color='text.secondary'
              >
                {t('landing.mobile.subtext')}
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              display='flex'
              flexDirection={'column'}
              justifyContent='center'
              alignItems='center'
            >
              <Box
                component='img'
                src={mobileShots[mobileSlide]}
                alt={`Bluu mobilapp ${mobileSlide + 1}`}
                sx={{
                  maxHeight: heroImageMaxHeight,
                  transition: 'opacity 0.4s ease',
                  width: '100%',
                  objectFit: 'contain',
                }}
              />
              <Stack
                direction='row'
                spacing={1}
                sx={{ height: 20, alignItems: 'center' }}
              >
                {mobileShots.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setMobileSlide(idx)}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      backgroundColor:
                        idx === mobileSlide
                          ? 'secondary.main'
                          : 'primary.lighter',
                      opacity: idx === mobileSlide ? 1 : 0.6,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={2.5}>
          <Grid
            container
            sx={{
              minHeight: 'auto',
              maxHeight: 'none',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              px: 1,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                minHeight: 40,
                maxHeight: 40,
                mb: 2,
              }}
            >
              <Chip
                label={t('landing.hero.tagline3', 'Start your journey now')}
                color='secondary'
                sx={{ width: 'fit-content', fontSize: chipFontsize }}
              />
            </Grid>
            <Grid item xs={12} sx={{}}>
              <Paper
                variant='elevation3'
                sx={{
                  p: 2,
                  borderRadius: 3,
                  height: height / 2 - 164,
                  background:
                    'linear-gradient(180deg, rgba(69,90,100,0.6) 0%, rgba(38,50,56,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.05,
                      letterSpacing: 2,
                      fontSize: rightFontsize * 1.4,
                    }}
                    color='text.secondary'
                  >
                    {t('landing.panel.title')}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      lineHeight: 1.05,
                      letterSpacing: 1,
                      fontSize: rightFontsize,
                    }}
                    color='text.secondary'
                  >
                    {t('landing.panel.body')}
                  </Typography>
                  {Array.isArray(bragLines) && (
                    <Stack spacing={2}>
                      {bragLines.map((line) => (
                        <Typography
                          key={line}
                          sx={{
                            fontWeight: 400,
                            lineHeight: 1.05,
                            letterSpacing: 1,
                            fontSize: rightFontsize,
                          }}
                          color='text.secondary'
                        >
                          • {line}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Button
                      variant='contained'
                      size='small'
                      color='secondary'
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/pricing')}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {t('landing.panel.cta')}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sx={{}}>
              <Paper
                variant='elevation3'
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  height: height / 2 - 88,
                  background:
                    'linear-gradient(180deg, rgba(69,90,100,0.6) 0%, rgba(38,50,56,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.05,
                    letterSpacing: 2,
                    mb: 2,
                    fontSize: rightFontsize * 1.4,
                  }}
                >
                  {t('landing.partners.header', {
                    defaultValue: 'MSP-partnere',
                  })}
                </Typography>
                <Stack
                  ref={partnerListRef}
                  spacing={2}
                  sx={{
                    maxHeight: {
                      xs: 200,
                      sm: 250,
                      md: partnerListHeight,
                      lg: partnerListHeight,
                    },
                    overflow: 'hidden',
                  }}
                >
                  {[
                    { name: 'SkyPilot MSP', stars: '*****', story: 'Bluu automates licensing and billing. We save hours every week and have tighter margins.' },
                    { name: 'Nordic CloudWorks', stars: '*****', story: 'The customer app gives real-time insight, so clients find answers themselves and support queues shrink.' },
                    { name: 'SecureOps Partner', stars: '*****', story: 'Seamless Microsoft licensing and tidy tickets mean we bill correctly the first time.' },
                    { name: 'BluePeak Services', stars: '*****', story: 'Everything from agreements to projects is in one place. The team moves faster because nothing is scattered.' },
                    { name: 'Aurora IT Drift', stars: '*****', story: 'Onboarding is twice as fast. Automated billing removed manual mistakes.' },
                    { name: 'CloudBridge Nordic', stars: '*****', story: 'Bluu is easy to onboard new techs with. Structure and templates mean nothing slips.' },
                    { name: 'Arctic Managed Services', stars: '*****', story: 'Real-time alerts and self-service in the app make customers feel informed and reduce email.' },
                    { name: 'MetroTech MSP', stars: '*****', story: 'One source for agreements, products, and invoices. Budgeting and reporting are much simpler.' },
                    { name: 'FjordOps', stars: '*****', story: 'From sale to delivery runs in Bluu. Less double-work, faster delivery.' },
                    { name: 'Nimbus IT Partner', stars: '*****', story: 'All communication is documented, reducing misunderstandings and conflicts.' },
                    { name: 'IT Innovators', stars: '*****', story: 'Support gets context and suggestions. We resolve cases faster and more consistently.' },
                    { name: 'Vertex Cloud Services', stars: '*****', story: 'Scales licensing and billing without adding admin. Perfect for growth.' },
                    { name: 'Pinnacle MSP', stars: '*****', story: 'Stable uptime and quick support. 99% availability is real for us in production.' },
                    { name: 'Summit IT Solutions', stars: '*****', story: 'Change requests are handled in the app. We price and deliver without email chaos.' },
                    { name: 'CrestPoint Technologies', stars: '*****', story: 'Bluu makes us proactive. SLAs and agreements are visible, so we fix issues before customers notice.' },
                  ].map((p) => (
                    <Stack
                      key={p.name}
                      spacing={0.5}
                      sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        pb: 1,
                      }}
                    >
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.05,
                            letterSpacing: 2,
                            fontSize: rightFontsize * 1.2,
                          }}
                        >
                          {p.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            lineHeight: 1.05,
                            letterSpacing: 2,
                            mt:2,
                            fontSize: rightFontsize * 2.2,
                          }}
                          color='secondary.main'
                        >
                          {p.stars}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          lineHeight: 1.05,
                          letterSpacing: 2,
                          fontSize: rightFontsize,
                        }}
                        color='text.secondary'
                      >
                        {p.story}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PublicShell>
  );
};

export default LandingPage;






